<template>
    <div class="ei-new">
        <section class="ei-new-section">
            <div class="ei-new-section-head">기본 정보</div>
            <div class="ei-new-grid">
                <label class="ei-new-field">
                    <span class="ei-new-label">카테고리 <em class="ei-req">*</em></span>
                    <select v-model="form.enIntCategory" class="ei-new-select">
                        <option value="" disabled>선택</option>
                        <option v-for="c in enIntCategoryItems" :key="c.value" :value="c.value">
                            {{ categoryLabelFromStoreItem(c) }}
                        </option>
                    </select>
                </label>
                <label class="ei-new-field">
                    <span class="ei-new-label">코드 <em class="ei-req">*</em></span>
                    <input
                        v-model.trim="form.enIntCode"
                        class="ei-new-input"
                        type="text"
                        maxlength="50"
                        placeholder="예: ENINT_PROD_LINE_A"
                    />
                </label>
                <label class="ei-new-field">
                    <span class="ei-new-label">이름 <em class="ei-req">*</em></span>
                    <input
                        v-model.trim="form.enIntName"
                        class="ei-new-input"
                        type="text"
                        maxlength="100"
                        placeholder="예: A라인 원단위"
                    />
                </label>
                <label class="ei-new-field">
                    <span class="ei-new-label">자동 계산</span>
                    <select v-model="form.calcEnabled" class="ei-new-select">
                        <option value="Y">사용</option>
                        <option value="N">미사용</option>
                    </select>
                </label>
                <label class="ei-new-field ei-new-field--full">
                    <span class="ei-new-label">기준값 입력 방식</span>
                    <select v-model="form.dataInputType" class="ei-new-select">
                        <option :value="ENINT_DATA_INPUT_NEW">신규 관제점(직접 생성)</option>
                        <option :value="ENINT_DATA_INPUT_EXISTING">기존 관제점(기준값 매핑)</option>
                    </select>
                </label>
                <label class="ei-new-field ei-new-field--full">
                    <span class="ei-new-label">비고</span>
                    <textarea v-model.trim="form.rmk" class="ei-new-textarea" rows="2" maxlength="500"></textarea>
                </label>
            </div>
        </section>

        <section class="ei-new-section">
            <div class="ei-new-section-head">기준 관제점</div>
            <p class="ei-new-flow-hint">대상 관제점 값 집계 ÷ 기준 관제점 값 → 결과 저장 관제점 저장</p>
            <template v-if="isNewPointInput">
                <p class="ei-new-hint">
                    관제점 주소·이름을 입력하면 서버에서 관제점을 생성하여 기준 관제점으로 연결합니다.
                </p>
                <div class="ei-new-grid">
                    <label class="ei-new-field">
                        <span class="ei-new-label">주소 <em class="ei-req">*</em></span>
                        <input
                            v-model.trim="basePoint.ptAddr"
                            class="ei-new-input"
                            type="text"
                            maxlength="50"
                            placeholder="예: ENINT.BASE.LINE_A"
                        />
                    </label>
                    <label class="ei-new-field">
                        <span class="ei-new-label">이름 <em class="ei-req">*</em></span>
                        <input
                            v-model.trim="basePoint.ptName"
                            class="ei-new-input"
                            type="text"
                            maxlength="50"
                            placeholder="예: A라인 생산(기준)"
                        />
                    </label>
                </div>
            </template>
            <template v-else>
                <p class="ei-new-hint">
                    기존 ENINT 그룹 관제점을 기준 관제점으로 선택합니다. 월 요약에서 기준값을 채울 수 있습니다.
                </p>
                <div class="ei-new-base-pick">
                    <button type="button" class="ei-new-btn ei-new-btn--ghost" @click="openBaseModal">
                        <i class="fas fa-search"></i> 관제점 찾기
                    </button>
                    <div v-if="existingBasePoint" class="ei-new-base-chip">
                        <strong>{{ existingBasePoint.ptName || "—" }}</strong>
                        <span class="ei-new-base-chip-addr">{{ existingBasePoint.ptAddr }}</span>
                        <span class="ei-new-base-chip-id">#{{ existingBasePoint.ptIdx }}</span>
                    </div>
                    <span v-else class="ei-new-muted">미선택</span>
                </div>
            </template>
        </section>

        <section class="ei-new-section ei-new-section--muted">
            <div class="ei-new-section-head">결과 저장 관제점</div>
            <p class="ei-new-hint">마스터 생성 시 서버에서 결과 저장 관제점(RESULT)을 자동 생성합니다.</p>
            <div class="ei-new-grid">
                <label class="ei-new-field">
                    <span class="ei-new-label">주소 <em class="ei-req">*</em></span>
                    <input
                        v-model.trim="resultPoint.ptAddr"
                        class="ei-new-input"
                        type="text"
                        maxlength="50"
                        placeholder="예: ENINT.RES.PROD.LINE_A"
                    />
                </label>
                <label class="ei-new-field">
                    <span class="ei-new-label">이름 <em class="ei-req">*</em></span>
                    <input
                        v-model.trim="resultPoint.ptName"
                        class="ei-new-input"
                        type="text"
                        maxlength="50"
                        placeholder="예: [원단위] A라인"
                    />
                </label>
            </div>
        </section>

        <section class="ei-new-section">
            <div class="ei-new-section-head-row">
                <span class="ei-new-section-head">대상 관제점</span>
                <div class="ei-new-section-actions">
                    <span class="ei-new-section-count">{{ refList.length }}건 선택</span>
                </div>
            </div>
            <div class="ei-new-pill-list">
                <div v-for="(r, idx) in refList" :key="r.ptIdx + '-' + idx" class="ei-new-ref-pill">
                    <span class="ei-new-ref-pill-name">{{ r.ptName || r.ptAddr }}</span>
                    <span class="ei-new-ref-pill-addr">{{ r.ptAddr }}</span>
                    <label class="ei-new-ref-pill-weight">
                        <span>가중치</span>
                        <input
                            v-model.number="r.weight"
                            class="ei-new-input ei-new-input--pill-weight"
                            type="number"
                            min="0.000001"
                            step="any"
                        />
                    </label>
                    <button type="button" class="ei-new-ref-pill-remove" title="삭제" @click="refList.splice(idx, 1)">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <button type="button" class="ei-new-ref-add-pill" @click="openRefModal">
                    <i class="fas fa-plus"></i> 관제점 추가
                </button>
            </div>
            <p v-if="!refList.length" class="ei-new-muted">
                대상 관제점이 없어도 등록은 가능합니다. (계산 시 대상 관제점 값 없음)
            </p>
        </section>

        <div class="ei-new-actions">
            <span class="ei-new-actions-hint">필수 입력값을 모두 채우면 저장할 수 있습니다.</span>
            <button type="button" class="ei-new-btn ei-new-btn--ghost" @click="$emit('cancel')">취소</button>
            <button
                type="button"
                class="ei-new-btn ei-new-btn--primary"
                :disabled="saving || !canSubmit"
                @click="submit"
            >
                <i v-if="saving" class="fas fa-spinner fa-spin"></i> 저장
            </button>
        </div>

        <EnIntPointSearchModal
            :visible.sync="showRefModal"
            :keyword.sync="refKeyword"
            :point-list-api-state="refPointListApiState"
            :hint-pt-groups="['ENSPLY']"
            title="대상 관제점 추가"
            subtitle="중복 추가는 자동 제외"
            @search="filterRef"
            @select="onSelectRef"
        />

        <EnIntPointSearchModal
            :visible.sync="showBaseModal"
            :keyword.sync="baseKeyword"
            :point-list-api-state="basePointListApiState"
            title="기준 관제점 (ENINT)"
            @search="filterBase"
            @select="onSelectBase"
        />
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import {
    mapEnIntCategoryState,
    ensureCommonCodes,
    categoryLabelFromStoreItem,
    ENINT_DATA_INPUT_NEW,
    ENINT_DATA_INPUT_EXISTING,
} from "../utils/enIntUi.js";
import EnIntPointSearchModal from "./EnIntPointSearchModal.vue";

export default {
    name: "EnIntMasterNewModal",
    components: { EnIntPointSearchModal },
    computed: {
        ...mapEnIntCategoryState(),
        isNewPointInput() {
            return String(this.form.dataInputType || "").trim() === ENINT_DATA_INPUT_NEW;
        },
        canSubmit() {
            if (!this.form.enIntCategory || !String(this.form.enIntCode || "").trim()) return false;
            if (!String(this.form.enIntName || "").trim()) return false;
            if (!String(this.resultPoint.ptAddr || "").trim() || !String(this.resultPoint.ptName || "").trim())
                return false;
            if (this.isNewPointInput) {
                if (!String(this.basePoint.ptAddr || "").trim() || !String(this.basePoint.ptName || "").trim())
                    return false;
            } else if (!this.existingBasePoint?.ptIdx) {
                return false;
            }
            return this.refList.every((r) => Number(r.weight) > 0);
        },
    },
    data() {
        return {
            ENINT_DATA_INPUT_NEW,
            ENINT_DATA_INPUT_EXISTING,
            saving: false,
            form: {
                enIntCategory: "",
                enIntCode: "",
                enIntName: "",
                calcEnabled: "Y",
                rmk: "",
                dataInputType: ENINT_DATA_INPUT_NEW,
            },
            basePoint: { ptAddr: "", ptName: "" },
            existingBasePoint: null,
            showBaseModal: false,
            baseKeyword: "",
            baseAllPoints: [],
            basePointListApiState: { response: null, data: { rows: [], totalCount: 0 }, loading: false, error: "" },
            resultPoint: { ptAddr: "", ptName: "" },
            refList: [],
            showRefModal: false,
            refKeyword: "",
            refAllPoints: [],
            refPointListApiState: { response: null, data: { rows: [], totalCount: 0 }, loading: false, error: "" },
        };
    },
    async mounted() {
        await ensureCommonCodes(this.$store);
    },
    watch: {
        "form.dataInputType"(v) {
            if (v === ENINT_DATA_INPUT_NEW) {
                this.existingBasePoint = null;
            } else {
                this.basePoint = { ptAddr: "", ptName: "" };
            }
        },
    },
    methods: {
        categoryLabelFromStoreItem,
        async openRefModal() {
            this.showRefModal = true;
            this.refKeyword = "";
            this.refPointListApiState = {
                ...this.refPointListApiState,
                loading: true,
                error: "",
            };
            try {
                const getPointList_ref_response = await backEndApi.pointInfo.getPointList();
                const getPointList_ref_data = Array.isArray(getPointList_ref_response?.data)
                    ? getPointList_ref_response.data
                    : [];
                this.refAllPoints = getPointList_ref_data
                    .filter((p) => String(p.enabled || "Y").toUpperCase() === "Y")
                    .map((p) => ({ ptIdx: p.ptIdx, ptAddr: p.ptAddr, ptName: p.ptName, ptGroup: p.ptGroup }));
                this.refPointListApiState = {
                    response: getPointList_ref_response,
                    data: { rows: [], totalCount: this.refAllPoints.length },
                    loading: false,
                    error: "",
                };
                this.applyRefFilter();
            } catch {
                this.refAllPoints = [];
                this.refPointListApiState = {
                    response: null,
                    data: { rows: [], totalCount: 0 },
                    loading: false,
                    error: "관제점 목록을 불러오지 못했습니다.",
                };
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            }
        },
        applyRefFilter() {
            const existing = new Set(this.refList.map((r) => r.ptIdx));
            const kw = this.refKeyword.trim().toLowerCase();
            let list = this.refAllPoints.filter((p) => !existing.has(p.ptIdx));
            if (kw) {
                list = list.filter(
                    (p) =>
                        (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                        (p.ptName && p.ptName.toLowerCase().includes(kw)),
                );
            }
            this.refPointListApiState = {
                ...this.refPointListApiState,
                data: { rows: list, totalCount: this.refAllPoints.length },
                loading: false,
            };
        },
        filterRef() {
            this.applyRefFilter();
        },
        onSelectRef(row) {
            if (this.refList.some((r) => r.ptIdx === row.ptIdx)) return;
            this.refList.push({ ptIdx: row.ptIdx, ptAddr: row.ptAddr, ptName: row.ptName, weight: 1 });
            this.showRefModal = false;
        },
        async openBaseModal() {
            this.showBaseModal = true;
            this.baseKeyword = "";
            this.basePointListApiState = {
                ...this.basePointListApiState,
                loading: true,
                error: "",
            };
            try {
                const getPointList_base_response = await backEndApi.pointInfo.getPointList();
                const getPointList_base_data = Array.isArray(getPointList_base_response?.data)
                    ? getPointList_base_response.data
                    : [];
                this.baseAllPoints = getPointList_base_data
                    .filter((p) => String(p.enabled || "Y").toUpperCase() === "Y")
                    // .filter((p) => String(p.ptGroup || "").toUpperCase() === "ENINT")
                    .map((p) => ({ ptIdx: p.ptIdx, ptAddr: p.ptAddr, ptName: p.ptName, ptGroup: p.ptGroup }));
                this.basePointListApiState = {
                    response: getPointList_base_response,
                    data: { rows: Array.from(this.baseAllPoints), totalCount: this.baseAllPoints.length },
                    loading: false,
                    error: "",
                };
            } catch {
                this.baseAllPoints = [];
                this.basePointListApiState = {
                    response: null,
                    data: { rows: [], totalCount: 0 },
                    loading: false,
                    error: "관제점 목록을 불러오지 못했습니다.",
                };
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            }
        },
        filterBase() {
            const kw = this.baseKeyword.trim().toLowerCase();
            if (!kw) {
                this.basePointListApiState = {
                    ...this.basePointListApiState,
                    data: { rows: Array.from(this.baseAllPoints), totalCount: this.baseAllPoints.length },
                    loading: false,
                };
                return;
            }
            const filtered = this.baseAllPoints.filter(
                (p) =>
                    (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                    (p.ptName && p.ptName.toLowerCase().includes(kw)),
            );
            this.basePointListApiState = {
                ...this.basePointListApiState,
                data: { rows: filtered, totalCount: this.baseAllPoints.length },
                loading: false,
            };
        },
        onSelectBase(row) {
            this.existingBasePoint = { ptIdx: row.ptIdx, ptAddr: row.ptAddr, ptName: row.ptName };
            this.showBaseModal = false;
        },
        validate() {
            if (!this.form.enIntCategory) return "카테고리를 선택하세요.";
            if (!this.form.enIntCode) return "코드를 입력하세요.";
            if (!this.form.enIntName) return "이름을 입력하세요.";
            if (this.isNewPointInput) {
                if (!String(this.basePoint.ptAddr || "").trim()) return "기준 관제점 주소를 입력하세요.";
                if (!String(this.basePoint.ptName || "").trim()) return "기준 관제점 이름을 입력하세요.";
            } else if (!this.existingBasePoint?.ptIdx) {
                return "기준 관제점으로 사용할 기존 관제점을 선택하세요.";
            }
            if (!String(this.resultPoint.ptAddr || "").trim()) return "결과 저장 관제점 주소를 입력하세요.";
            if (!String(this.resultPoint.ptName || "").trim()) return "결과 저장 관제점 이름을 입력하세요.";
            for (let i = 0; i < this.refList.length; i += 1) {
                if (!(Number(this.refList[i].weight) > 0)) return "가중치는 0보다 커야 합니다.";
            }
            return "";
        },
        async submit() {
            const msg = this.validate();
            if (msg) {
                this.alertNoti(msg);
                return;
            }
            this.saving = true;
            try {
                const body = {
                    enIntCategory: this.form.enIntCategory,
                    enIntCode: this.form.enIntCode,
                    enIntName: this.form.enIntName,
                    dataInputType: this.form.dataInputType,
                    resultPoint: { ptAddr: this.resultPoint.ptAddr, ptName: this.resultPoint.ptName },
                    calcEnabled: this.form.calcEnabled,
                    rmk: this.form.rmk || null,
                    refList: this.refList.map((r) => ({ ptIdx: r.ptIdx, weight: Number(r.weight) || 1 })),
                };
                if (this.isNewPointInput) {
                    body.basePoint = { ptAddr: this.basePoint.ptAddr, ptName: this.basePoint.ptName };
                } else {
                    body.basePtIdx = this.existingBasePoint.ptIdx;
                }
                const insertMaster_response = await backEndApi.enInt.insertMaster(body);
                const insertMaster_data = insertMaster_response?.data ?? null;
                const enIntIdx = insertMaster_data?.enIntIdx;
                if (!enIntIdx) throw new Error("api");
                this.alertNoti("등록되었습니다.");
                this.$emit("saved", enIntIdx);
            } catch (error) {
                console.error("등록에러", error);
                this.alertNoti("등록에 실패했습니다.");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.ei-new {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ei-new-intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(14, 116, 144, 0.16);
    background: linear-gradient(135deg, rgba(14, 116, 144, 0.07), rgba(14, 116, 144, 0.03));
}

.ei-new-intro-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.ei-new-intro-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}

.ei-new-intro-desc {
    margin: 0;
    font-size: 11px;
    color: #475569;
}

.ei-new-intro-meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.ei-new-pill {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background: rgba(255, 255, 255, 0.9);
    font-size: 10px;
    font-weight: 700;
    color: #475569;
}

.ei-new-section {
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    padding: 12px 14px;
}

.ei-new-section--muted {
    background: rgba(248, 250, 252, 0.95);
}

.ei-new-section-head {
    font-size: 13px;
    font-weight: 700;
    color: #1b263b;
    margin-bottom: 8px;
}

.ei-new-section-head-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.ei-new-section-head-row .ei-new-section-head {
    margin-bottom: 0;
}

.ei-new-section-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.ei-new-section-count {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(203, 213, 225, 0.85);
    background: #fff;
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
}

.ei-new-hint {
    margin: 0 0 8px;
    font-size: 11px;
    color: #64748b;
    line-height: 1.5;
}

.ei-new-flow-hint {
    margin: 0 0 10px;
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid rgba(14, 116, 144, 0.16);
    background: rgba(14, 116, 144, 0.06);
    font-size: 11px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.45;
}

.ei-new-base-pick {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.ei-new-base-chip {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid rgba(14, 116, 144, 0.2);
    background: rgba(14, 116, 144, 0.06);
    font-size: 11px;
    color: #0f172a;
}

.ei-new-base-chip-addr {
    color: #64748b;
    font-weight: 600;
}

.ei-new-base-chip-id {
    font-size: 10px;
    font-weight: 700;
    color: #0e7490;
}

.ei-new-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px 14px;
}

.ei-new-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.ei-new-field--full {
    grid-column: 1 / -1;
}

.ei-new-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.ei-req {
    color: #dc2626;
    font-style: normal;
}

.ei-new-input,
.ei-new-select,
.ei-new-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 9px;
    border: 1px solid rgba(203, 213, 225, 0.8);
    border-radius: 6px;
    font-size: 12px;
    color: #1b263b;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
}

.ei-new-input:focus,
.ei-new-select:focus,
.ei-new-textarea:focus {
    border-color: rgba(14, 116, 144, 0.4);
}

.ei-new-textarea {
    resize: vertical;
}

.ei-new-pill-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.ei-new-ref-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(14, 116, 144, 0.2);
    background: rgba(14, 116, 144, 0.04);
    font-size: 11px;
    color: #334155;
}

.ei-new-ref-pill-name {
    font-weight: 700;
    max-width: 140px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.ei-new-ref-pill-addr {
    color: #64748b;
    font-size: 10px;
}

.ei-new-ref-pill-weight {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #64748b;
    margin-bottom: 0px;
}

.ei-new-input--pill-weight {
    width: 72px;
    padding: 3px 6px;
    font-size: 10px;
    border-radius: 999px;
}

.ei-new-ref-pill-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: rgba(148, 163, 184, 0.18);
    color: #64748b;
    font-size: 8px;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;
    padding: 0;
    flex-shrink: 0;
}

.ei-new-ref-pill-remove:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
}

.ei-new-ref-add-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px dashed rgba(14, 116, 144, 0.3);
    background: transparent;
    color: #0e7490;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.15s,
        border-color 0.15s;
}

.ei-new-ref-add-pill:hover {
    background: rgba(14, 116, 144, 0.06);
    border-color: rgba(14, 116, 144, 0.45);
}

.ei-new-muted {
    font-size: 12px;
    color: #94a3b8;
    padding: 4px 0;
}

.ei-new-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 4px;
}

.ei-new-actions-hint {
    margin-right: auto;
    font-size: 11px;
    color: #64748b;
}

.ei-new-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
}

.ei-new-btn--primary {
    background: #0e7490;
    color: #fff;
    border-color: #0e7490;
}

.ei-new-btn--primary:hover {
    background: #0c6a82;
}

.ei-new-btn--primary:disabled {
    opacity: 0.5;
    cursor: default;
}

.ei-new-btn--ghost {
    background: transparent;
    color: #475569;
    border-color: rgba(226, 232, 240, 0.9);
}

.ei-new-btn--ghost:hover {
    background: rgba(241, 245, 249, 0.9);
}

@media (max-width: 720px) {
    .ei-new-section-head-row {
        flex-direction: column;
        align-items: stretch;
    }

    .ei-new-section-actions {
        justify-content: space-between;
    }

    .ei-new-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .ei-new-actions-hint {
        margin-right: 0;
    }

    .ei-new-btn {
        justify-content: center;
    }

    .ei-new-ref-pill {
        flex-wrap: wrap;
        border-radius: 12px;
    }

    .ei-new-ref-pill-weight {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
